"use client";

// Per-message error/warning indicator (plan 043 P6, gap-audit T5), moved
// from the private `@magnus/react` transcript so every block host shows a
// persisted failure the same way. The block renders it by default through
// `renderMessageDebug` when the host passes nothing; the flagship composes
// the same component with its debug panel through that one slot.
import { c as _c } from "react/compiler-runtime";
import { classifyMessageErrorTier, getMessageErrorTitle, type MagnusUIMessage, type MessageErrorCode } from "@bg-bgi/magnus-sdk";
import { AlertTriangle, Check, Clock, Coins, Flag, StopCircle, Wrench } from "lucide-react";

/** True for an assistant turn the workflow persisted as a failure — the
 *  rows the indicator (and the transport banner's stand-down) key off. */
export function isErroredAssistantMessage(message: MagnusUIMessage): boolean {
  return message.role === "assistant" && message.metadata?.status === "error";
}

/**
 * "Report error" affordance rendered inside error/warning notices. Same
 * mechanics as the thumbs-down button elsewhere — opens the negative
 * feedback modal. Styled as a low-profile inline button that inherits the
 * notice's color (via `currentColor`) so it reads as part of the notice
 * rather than a generic action.
 *
 * Once feedback has been submitted for the message, the button flips to a
 * disabled "Reported" state with a check icon to confirm the action took.
 */
function ReportErrorButton(t0) {
  const $ = _c(6);
  const {
    onClick,
    hasReported
  } = t0;
  let t1;
  if ($[0] !== hasReported) {
    t1 = hasReported ? <><Check aria-hidden={true} className="size-3.5" />Reported</> : <><Flag aria-hidden={true} className="size-3.5" />Report error</>;
    $[0] = hasReported;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hasReported || $[3] !== onClick || $[4] !== t1) {
    t2 = <button className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-warning/30 px-2 py-1 font-medium text-xs transition-colors hover:bg-warning/15 disabled:cursor-default disabled:opacity-70 disabled:hover:bg-transparent" disabled={hasReported} onClick={onClick} type="button">{t1}</button>;
    $[2] = hasReported;
    $[3] = onClick;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}

/** Calm one-line note (user stop, per-turn caps) — no report affordance. */
function InlineNote(t0) {
  const $ = _c(8);
  const {
    icon: Icon,
    label,
    status
  } = t0;
  const t1 = status ? "status" : undefined;
  let t2;
  if ($[0] !== Icon) {
    t2 = <Icon aria-hidden={true} className="size-3" />;
    $[0] = Icon;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label) {
    t3 = <span>{label}</span>;
    $[2] = label;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = <div className="mt-2 flex items-center gap-1.5 text-muted-foreground text-xs" role={t1}>{t2}{t3}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}

/**
 * The notice under a failed assistant turn. Copy comes from the persisted
 * metadata: `errorCode` picks the tier and headline, `errorMessage` (already
 * sanitized server-side) is the body. `onReportError` is optional — omit it
 * for viewers who can't leave feedback and the notice stays read-only.
 */
export function MessageErrorIndicator(t0) {
  const $ = _c(24);
  const {
    errorCode,
    errorMessage,
    onReportError,
    hasReported
  } = t0;
  let t1;
  if ($[0] !== errorCode) {
    t1 = classifyMessageErrorTier(errorCode);
    $[0] = errorCode;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tier = t1;
  if (tier === "quiet") {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <InlineNote icon={StopCircle} label="Response stopped" />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  if (errorCode === "cost_limit") {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <InlineNote icon={Coins} label={"This response reached its size limit \u2014 send another message to continue."} status={true} />;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (errorCode === "step_limit") {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <InlineNote icon={Wrench} label={"Tool-call limit for this turn reached \u2014 ask me to continue."} status={true} />;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  const isWarning = tier === "warning";
  const Icon = errorCode === "turn_timeout" || errorCode === "provider_rate_limit" ? Clock : AlertTriangle;
  let t2;
  if ($[5] !== errorCode || $[6] !== isWarning) {
    t2 = getMessageErrorTitle(errorCode) ?? (isWarning ? "Response stopped" : "Something went wrong \u2014 please try again");
    $[5] = errorCode;
    $[6] = isWarning;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const title = t2;
  const body = errorMessage ?? (isWarning ? undefined : "This is usually temporary. Please try again.");
  let t3;
  if ($[8] !== Icon) {
    t3 = <Icon aria-hidden={true} className="mt-0.5 size-4 shrink-0" />;
    $[8] = Icon;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== title) {
    t4 = <p className="font-medium text-sm">{title}</p>;
    $[10] = title;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== body) {
    t5 = body && <p className="mt-0.5 text-sm text-warning/80">{body}</p>;
    $[12] = body;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4 || $[15] !== t5) {
    t6 = <div className="min-w-0 flex-1">{t4}{t5}</div>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== hasReported || $[18] !== onReportError) {
    t7 = onReportError && <ReportErrorButton hasReported={hasReported} onClick={onReportError} />;
    $[17] = hasReported;
    $[18] = onReportError;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t3 || $[21] !== t6 || $[22] !== t7) {
    t8 = <div className="mt-2 flex items-start gap-3 rounded-md border border-warning/20 bg-warning/10 p-3 text-warning" role="status">{t3}{t6}{t7}</div>;
    $[20] = t3;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}